import { Vector3 } from '@/math'
import { stringify, parse } from 'devalue'
import type { VoxelData } from '@/voxelTypes'
import { ChunkedVoxelGrid, isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { VoxelPalette } from '@/storage/grid/palette'
import { keyOf } from '@/storage/grid/layout'

// Custom type reducers and revivers for devalue.
// Vector3 appears in event payloads (normals / exact points); chunked grids appear as checkpoint
// bases in AppEventLog.serialize (never inside event payloads; see the dev assertion there).

// Grid wire form: bounds, the palette items in id order (id = index + 1), and flat [key, id, ...] cells.
type GridWire = { b: [number, number, number]; p: VoxelData[]; c: number[] }

function gridToWire(g: ChunkedVoxelGrid): GridWire {
  const p = g.palette.items.slice(1) as VoxelData[]
  const c: number[] = []
  g.forEachChunk((chunkId, chunk) => {
    if (chunk.count === 0) return
    const d = chunk.data
    for (let i = 0; i < 4096; i++) if (d[i] !== 0) c.push(keyOf(chunkId, i), d[i])
  })
  return { b: [g.bounds.x, g.bounds.y, g.bounds.z], p, c }
}

// Palette shared by every grid revived within one parse call, so checkpoints of one log stay comparable.
let revivePalette: VoxelPalette | null = null

function gridFromWire(w: GridWire): ChunkedVoxelGrid {
  const palette = revivePalette ?? new VoxelPalette()
  const ids = w.p.map((v) => palette.intern(v))
  const g = new ChunkedVoxelGrid(palette, { x: w.b[0], y: w.b[1], z: w.b[2] }).asMutable()
  const c = w.c
  for (let i = 0; i + 1 < c.length; i += 2) {
    const id = ids[c[i + 1] - 1]
    if (id !== undefined) g.setId(c[i], id)
  }
  return g.asImmutable()
}

/**
 * Legacy checkpoint bases (before plan S13) were Immutable.Map instances serialized as `ImMap`
 * entries `[[key, voxel], ...]`. They are revived straight into a grid on the shared revive palette.
 * The wire shape carries no bounds, so the grid spans the whole domain; `AppEventLog.deserialize`
 * clips each checkpoint to its `meta.bounds` afterwards (`withDocumentBounds`). Nothing writes this
 * shape any more.
 */
function gridFromLegacyEntries(entries: Array<[unknown, unknown]>): ChunkedVoxelGrid {
  const g = new ChunkedVoxelGrid(revivePalette ?? new VoxelPalette()).asMutable()
  for (const [key, voxel] of entries) {
    if (typeof key !== 'number' || !voxel || typeof voxel !== 'object') continue
    g.set(key, voxel as VoxelData)
  }
  return g.asImmutable()
}

export const DevalueTypes = {
  Vector3: (value: unknown) => value instanceof Vector3 && [value.x, value.y, value.z],
  ChunkedVoxelGrid: (value: unknown) => isChunkedVoxelGrid(value) && gridToWire(value),
}

export const DevalueRevivers = {
  Vector3: ([x, y, z]: [number, number, number]) => new Vector3(x, y, z),
  ChunkedVoxelGrid: (wire: GridWire) => gridFromWire(wire),
  ImMap: (entries: Array<[unknown, unknown]>) => gridFromLegacyEntries(entries),
}

export function encodeDevalue(value: unknown): string {
  return stringify(value, DevalueTypes)
}

export function decodeDevalue<T>(text: string): T {
  const previous = revivePalette
  revivePalette = new VoxelPalette()
  try {
    return parse(text, DevalueRevivers) as T
  } finally {
    revivePalette = previous
  }
}
